import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import { useState } from 'react';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import TaxExtensionsBulkUploadTemplate from '@/components/ui/TaxExtensionBulkUploadTemplate';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form4868BulkUpload = () => {
  const [isBulkUploadTemplate, setIsBulkUploadTemplate] = useState(false);
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 4868 Bulk Upload | File 1040 Extension in Bulk"
          description="Learn how to use TaxZerone’s Form 4868 Bulk Upload to file multiple personal tax extensions easily. Upload Excel files, validate data, and transmit the return. "
          canonical="resources/tax-extension/form-4868-bulk-upload/"
          ogType="article"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <TaxExtensionsBulkUploadTemplate
        isBulkUploadTemplate={isBulkUploadTemplate}
        setIsBulkUploadTemplate={setIsBulkUploadTemplate}
        formType="4868"
      />
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          Form 4868{' '}
          <span className="highlight-container">
            <span className="highlight"> Bulk Upload </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          TaxZerone’s Bulk Upload feature helps users to file multiple 4868 Form
          in one go.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868bulk`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-4868-bulk-upload/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 4868 Bulk Upload at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          Form 4868 Bulk Upload
                        </span>{' '}
                        allows tax professionals to prepare{' '}
                        <span style={{ fontWeight: 600 }}>
                          multiple individual tax extension returns
                        </span>{' '}
                        using a single Excel file, reducing the need for
                        repetitive manual data entry.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          Tax preparers, CPA firms, enrolled agents, accounting
                          firms, and tax service providers
                        </span>{' '}
                        can use the bulk upload process to manage Form 4868 data
                        for multiple clients.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          TaxZerone accepts Excel (.xlsx) files
                        </span>{' '}
                        for Form 4868 bulk uploads. Users can download the
                        preformatted template, enter the required taxpayer
                        information, and upload the completed file.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          The Excel file must be 3 MB or smaller
                        </span>{' '}
                        to be uploaded through the bulk upload process.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}>
                          Uploaded records can be reviewed for errors
                        </span>{' '}
                        before users proceed with preparing and filing the
                        individual Form 4868 returns.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Managing high volumes of extension requests doesn’t have to be
                  complex. With TaxZerone’s{' '}
                  <span style={{ fontWeight: 600 }}>Form 4868 Bulk Upload</span>
                  , you can process multiple personal tax extensions in minutes
                  — quickly and efficiently.
                </Typography>
                <Box>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 4868 Bulk Upload?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whocan" sx={{ color: '#0202ff' }}>
                          Who can use Bulk Upload?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          What file formats will be accepted?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#steps" sx={{ color: '#0202ff' }}>
                          Steps to Bulk Upload Form 4868 with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#benefits" sx={{ color: '#0202ff' }}>
                          Advantages of Form 4868 Bulk Upload with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is Form 4868 Bulk Upload?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  TaxZerone’s Bulk Upload feature allows you to prepare and
                  submit multiple 4868 IRS form using a{' '}
                  <span style={{ fontWeight: 600 }}>single excel file</span>.
                  Instead of repetitive data entry, you can upload all client
                  details at once and complete filings in minutes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                  id="whocan"
                >
                  Who can use Bulk Upload?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  This extension applies to the following{' '}
                  <span style={{ fontWeight: 600 }}>
                    federal income tax returns:
                  </span>
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Tax preparers </span>{' '}
                      handling multiple individual clients
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> CPA firms </span> –
                      managing large client portfolios 
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Enrolled Agents (EAs), ERO and Reporting Agent
                      </span>{' '}
                      – representing individual taxpayers before the IRS
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Tax consultants and advisors{' '}
                      </span>{' '}
                      – offering extension filing services
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Accounting firms and staff
                      </span>{' '}
                      processing multiple 1040 extensions
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Bookkeeping firms
                      </span>{' '}
                      supporting individual clients 
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Tax service providers
                      </span>{' '}
                      – offering bulk e-filing solutions
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="file"
                >
                  What file formats will be accepted?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone accepts{' '}
                  <span style={{ fontWeight: 600 }}>Excel (.xlsx)</span>file
                  format for bulk uploads. You can either upload your Excel file
                  directly, or if your file is in a different format, you can
                  use the{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?utm_source=resource&utm_campaign=form+4868+bulk+upload`}

                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Secure File Upload
                  </Link>{' '}
                  —our automatic file converter will generate your file in the
                  required format.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { md: 17, sm: 17, xs: 16 },
                          fontWeight: 600,
                        }}
                      >
                        Form 4868 Bulk Upload Template
                      </Typography>
                      <Typography variant="body1">
                        Skip the manual entry. Use our preformatted 4868 Excel
                        template to file for multiple clients in minutes — just
                        fill and upload.
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        ✓ Free to use ✓ Minimizes errors ✓ Saves time
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        onClick={() => {
                          setIsBulkUploadTemplate(true);
                        }}
                      >
                        Download Template
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="steps"
                >
                  Steps to Bulk Upload Form 4868 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Follow these steps to bulk upload your{' '}
                  <Link
                    href="/resources/tax-extension/form-4868-instructions"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 4868 online
                  </Link>{' '}
                  efficiently:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signin/?utm_source=resource&utm_campaign=form+4868+bulk+upload`}
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Sign in
                  </Link>{' '}
                  TaxZerone account using your credentials. From the dashboard,
                  click <span style={{ fontWeight: 600 }}>“Start”</span> on the
                  right side and select{' '}
                  <span style={{ fontWeight: 600 }}> “New Form” </span> from the
                  dropdown menu to proceed.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-bulk-upload-dashboard-img.webp" />
                  <source srcSet="/assets/images/4868-bulk-upload-dashboard-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-bulk-upload-dashboard-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={350}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 2:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Go to the{' '}
                  <span style={{ fontWeight: 600 }}>Extension Forms</span>{' '}
                  section and select{' '}
                  <span style={{ fontWeight: 600 }}>Form 4868 Bulk Upload</span>{' '}
                  to get started with your filing.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-bulk-started-img.webp" />
                  <source srcSet="/assets/images/4868-bulk-started-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-bulk-started-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={450}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Download the Form 4868 bulk upload template in Excel format.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-bulk-img.webp" />
                  <source srcSet="/assets/images/4868-bulk-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-bulk-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={500}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Fill in the required details, including primary SSN, name, tax
                  liability, and other relevant details — then save your file.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-bulk-template-img.webp" />
                  <source srcSet="/assets/images/4868-bulk-template-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-bulk-template-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={320}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 5:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select your completed Form 4868 Excel file from your device
                  (make sure it’s{' '}
                  <span style={{ fontWeight: 600 }}>3 MB or smaller</span>) then
                  click <span style={{ fontWeight: 600 }}>“Upload”</span> to
                  process and prepare your filings automatically .
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-bulk-upload-img.webp" />
                  <source srcSet="/assets/images/4868-bulk-upload-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-bulk-upload-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={575}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 6:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Review your uploaded details to make sure everything matches
                  the information shown in the table, then click{' '}
                  <span style={{ fontWeight: 600 }}>“Process File”</span> to
                  continue.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-process-file-img.webp" />
                  <source srcSet="/assets/images/4868-process-file-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-process-file-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={360}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 7:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Check the results of your Form 4868 bulk upload to see if
                    any records have errors, and fix them if needed.
                  </li>
                  <li>
                    {' '}
                    If everything looks good, click{' '}
                    <span style={{ fontWeight: 600 }}>
                      ‘Go to Dashboard’
                    </span>{' '}
                    to view your returns and continue with filing.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/4868-success-file-img.webp" />
                  <source srcSet="/assets/images/4868-success-file-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-success-file-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={400}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 8:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Click{' '}
                  <span style={{ fontWeight: 600 }}>“Continue Filing”</span> to
                  proceed and submit each filing individually.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-continue-filing-img.webp" />
                  <source srcSet="/assets/images/4868-continue-filing-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-continue-filing-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={280}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Bulk Filing is not available at the moment in
                  TaxZerone.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 9:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Your Form 4868 details will be auto-filled—just review them to
                  make sure everything is correct, then click{' '}
                  <span style={{ fontWeight: 600 }}>“Save & Continue.”</span>
                </Typography>
                <picture>
                  <source srcSet="/assets/images/4868-auto-filled-img.webp" />
                  <source srcSet="/assets/images/4868-auto-filled-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-auto-filled-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={550}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 10:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Check the box to use your credits and{' '}
                    <Link
                      href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      e-file Form 4868
                    </Link>{' '}
                    quickly.
                  </li>
                  <li>
                    {' '}
                    You can also select{' '}
                    <span style={{ fontWeight: 600 }}>
                      any add-on services{' '}
                    </span>{' '}
                    if needed.
                  </li>
                  <li>
                    {' '}
                    Once you’re ready, click{' '}
                    <span style={{ fontWeight: 600 }}>
                      “E-File with IRS"{' '}
                    </span>{' '}
                    to submit your 4868 returns.
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="maximum-shareholders-note"
                >
                  <b>Note :</b> Refer our support article for step-by-step
                  instructions on{' '}
                  <Link
                    href="https://support.taxzerone.com/how-to-buy-extension-forms-credits-with-taxzerone/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    purchasing extension Form credits with TaxZerone
                  </Link>
                  .
                </Alert>
                <picture>
                  <source srcSet="/assets/images/4868-checkout-img.webp" />
                  <source srcSet="/assets/images/4868-checkout-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/4868-checkout-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={420}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="benefits"
                >
                  Advantages of IRS Form 4868 Bulk Upload with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  TaxZerone’s Form 4868 Bulk Upload is designed for efficiency
                  to:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 0.5 } }}>
                  <li>Save time by eliminating repetitive manual entry</li>

                  <li>Reduce filing errors with automated validation </li>

                  <li>
                    Improve productivity for firms handling multiple clients
                  </li>

                  <li>Simplify high-volume extension management</li>

                  <li>Speeds up submission and acknowledgment process</li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to Simplify Your 4868 Form Filing?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                maxWidth: { xs: '100%', md: '50%' },
                mx: 'auto',
              }}
            >
              Upload a single Excel file and manage multiple extension filings
              effortlessly with TaxZerone
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868bulk`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form4868BulkUpload;
